/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Application } from '../models/Application';
import type { ApplicationAccessList } from '../models/ApplicationAccessList';
import type { ApplicationAccessListCreate } from '../models/ApplicationAccessListCreate';
import type { ApplicationCreate } from '../models/ApplicationCreate';
import type { ApplicationDetail } from '../models/ApplicationDetail';
import type { ApplicationLicense } from '../models/ApplicationLicense';
import type { ApplicationLog } from '../models/ApplicationLog';
import type { ApplicationRepositoryDetail } from '../models/ApplicationRepositoryDetail';
import type { ApplicationUpdate } from '../models/ApplicationUpdate';
import type { ApplicationUserDetails } from '../models/ApplicationUserDetails';
import type { ApplicationValidate } from '../models/ApplicationValidate';
import type { LoggingApplications } from '../models/LoggingApplications';
import type { OperationStatus } from '../models/OperationStatus';
import type { PaginatedApplicationAccessListList } from '../models/PaginatedApplicationAccessListList';
import type { PaginatedApplicationAccessReadList } from '../models/PaginatedApplicationAccessReadList';
import type { PaginatedApplicationLicenseList } from '../models/PaginatedApplicationLicenseList';
import type { PaginatedApplicationList } from '../models/PaginatedApplicationList';
import type { PaginatedApplicationLogListList } from '../models/PaginatedApplicationLogListList';
import type { PaginatedApplicationRepositoryList } from '../models/PaginatedApplicationRepositoryList';
import type { PaginatedOrganizationTeamShortList } from '../models/PaginatedOrganizationTeamShortList';
import type { PaginatedUserShortList } from '../models/PaginatedUserShortList';
import type { PatchedApplicationUpdate } from '../models/PatchedApplicationUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ApplicationsService {

  /**
   * @param requestBody 
   * @returns Application 
   * @throws ApiError
   */
  public static applicationsCreate(
requestBody: ApplicationCreate,
): CancelablePromise<Application> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/applications/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param organizationId 
   * @param requestBody 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @returns PaginatedApplicationAccessReadList 
   * @throws ApiError
   */
  public static applicationsAccessCreate(
organizationId: number,
requestBody: ApplicationAccessListCreate,
page?: number,
perPage?: number,
): CancelablePromise<PaginatedApplicationAccessReadList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/applications/access/{organization_id}/',
      path: {
        'organization_id': organizationId,
      },
      query: {
        'page': page,
        'per_page': perPage,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ids comma separated list
   * @param instanceType 
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedApplicationList 
   * @throws ApiError
   */
  public static applicationsApplicationsList(
ids?: string,
instanceType?: number,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
): CancelablePromise<PaginatedApplicationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications/',
      query: {
        'ids': ids,
        'instance_type': instanceType,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
      },
    });
  }

  /**
   * @param application 
   * @param ids comma separated list
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param role 
   * @param search A search term.
   * @param user 
   * @returns PaginatedApplicationAccessListList 
   * @throws ApiError
   */
  public static applicationsApplicationsAccessList(
application?: number,
ids?: string,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
role?: string,
search?: string,
user?: number,
): CancelablePromise<PaginatedApplicationAccessListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-access/',
      query: {
        'application': application,
        'ids': ids,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'role': role,
        'search': search,
        'user': user,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Acces Aplicație.
   * @returns ApplicationAccessList 
   * @throws ApiError
   */
  public static applicationsApplicationsAccessRetrieve(
id: number,
): CancelablePromise<ApplicationAccessList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-access/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param active 
   * @param instanceType 
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedApplicationLicenseList 
   * @throws ApiError
   */
  public static applicationsApplicationsLicenseList(
active?: boolean,
instanceType?: number,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
): CancelablePromise<PaginatedApplicationLicenseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-license/',
      query: {
        'active': active,
        'instance_type': instanceType,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this application license.
   * @returns ApplicationLicense 
   * @throws ApiError
   */
  public static applicationsApplicationsLicenseRetrieve(
id: number,
): CancelablePromise<ApplicationLicense> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-license/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * List application audit logs
   * Retrieve a paginated list of application audit logs with filtering and sorting capabilities.
   * @param applicationId Filter by application ID
   * @param applicationName Filter by application name
   * @param instance Filter by instance ID
   * @param model Filter by table name
   * @param ordering Order by field. Prefix with '-' for descending order. Available fields: timestamp, type, application_name, modifier, model
   * @param organizationId Filter by organization ID
   * @param page Page number for pagination
   * @param pageSize Number of items per page
   * @param perPage Number of results to return per page.
   * @param search Search in description, modifier email, and modifier full name
   * @param timestampGte Filter logs from this timestamp (inclusive)
   * @param timestampLte Filter logs up to this timestamp (inclusive)
   * @param type Filter by log type
   * @param userId Filter by user ID
   * @returns PaginatedApplicationLogListList 
   * @throws ApiError
   */
  public static applicationsApplicationsLogList(
applicationId?: number,
applicationName?: string,
instance?: string,
model?: string,
ordering?: string,
organizationId?: number,
page?: number,
pageSize?: number,
perPage?: number,
search?: string,
timestampGte?: string,
timestampLte?: string,
type?: string,
userId?: number,
): CancelablePromise<PaginatedApplicationLogListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-log/',
      query: {
        'application_id': applicationId,
        'application_name': applicationName,
        'instance': instance,
        'model': model,
        'ordering': ordering,
        'organization_id': organizationId,
        'page': page,
        'page_size': pageSize,
        'per_page': perPage,
        'search': search,
        'timestamp_gte': timestampGte,
        'timestamp_lte': timestampLte,
        'type': type,
        'user_id': userId,
      },
    });
  }

  /**
   * Applications audit log from common log DB (Mongo)
   * @param id Audit Log ID
   * @returns ApplicationLog 
   * @throws ApiError
   */
  public static applicationsApplicationsLogRetrieve(
id: string,
): CancelablePromise<ApplicationLog> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-log/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Applications that have a log
   * @returns LoggingApplications 
   * @throws ApiError
   */
  public static applicationsApplicationsLogLoggingApplicationsList(): CancelablePromise<Array<LoggingApplications>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-log/logging-applications/',
    });
  }

  /**
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedApplicationRepositoryList 
   * @throws ApiError
   */
  public static applicationsApplicationsRepositoriesList(
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
): CancelablePromise<PaginatedApplicationRepositoryList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-repositories/',
      query: {
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Depozit de Aplicații.
   * @returns ApplicationRepositoryDetail 
   * @throws ApiError
   */
  public static applicationsApplicationsRepositoriesRetrieve(
id: number,
): CancelablePromise<ApplicationRepositoryDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications-repositories/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Aplicație.
   * @returns ApplicationDetail 
   * @throws ApiError
   */
  public static applicationsApplicationsRetrieve(
id: number,
): CancelablePromise<ApplicationDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/applications/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Aplicație.
   * @param requestBody 
   * @returns ApplicationUpdate 
   * @throws ApiError
   */
  public static applicationsApplicationsUpdate(
id: number,
requestBody?: ApplicationUpdate,
): CancelablePromise<ApplicationUpdate> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/applications/applications/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this Aplicație.
   * @param requestBody 
   * @returns ApplicationUpdate 
   * @throws ApiError
   */
  public static applicationsApplicationsPartialUpdate(
id: number,
requestBody?: PatchedApplicationUpdate,
): CancelablePromise<ApplicationUpdate> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/applications/applications/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param appToken 
   * @returns OperationStatus 
   * @throws ApiError
   */
  public static applicationsRemoveDestroy(
appToken: string,
): CancelablePromise<OperationStatus> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/applications/remove/{app_token}/',
      path: {
        'app_token': appToken,
      },
    });
  }

  /**
   * @param appToken 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @returns PaginatedOrganizationTeamShortList 
   * @throws ApiError
   */
  public static applicationsTeamListList(
appToken: string,
page?: number,
perPage?: number,
): CancelablePromise<PaginatedOrganizationTeamShortList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/team/{app_token}/list/',
      path: {
        'app_token': appToken,
      },
      query: {
        'page': page,
        'per_page': perPage,
      },
    });
  }

  /**
   * @param appToken 
   * @returns any 
   * @throws ApiError
   */
  public static applicationsUserCanAccessRetrieve(
appToken: string,
): CancelablePromise<{
access?: boolean;
}> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/user/{app_token}/can-access/',
      path: {
        'app_token': appToken,
      },
    });
  }

  /**
   * @param appToken 
   * @returns ApplicationUserDetails 
   * @throws ApiError
   */
  public static applicationsUserDetailsRetrieve(
appToken: string,
): CancelablePromise<ApplicationUserDetails> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/user/{app_token}/details',
      path: {
        'app_token': appToken,
      },
    });
  }

  /**
   * @param appToken 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedUserShortList 
   * @throws ApiError
   */
  public static applicationsUserListList(
appToken: string,
page?: number,
perPage?: number,
search?: string,
): CancelablePromise<PaginatedUserShortList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/user/{app_token}/list/',
      path: {
        'app_token': appToken,
      },
      query: {
        'page': page,
        'per_page': perPage,
        'search': search,
      },
    });
  }

  /**
   * @param appToken 
   * @returns ApplicationValidate 
   * @throws ApiError
   */
  public static applicationsValidateRetrieve(
appToken: string,
): CancelablePromise<ApplicationValidate> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/applications/validate/{app_token}/',
      path: {
        'app_token': appToken,
      },
    });
  }

}
